import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Gauge, Lock, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChatbot } from "@/context/ChatbotContext";
import { listRecords } from "@/lib/records";

// Health Score history graph — reads the signed-in user's private server-side
// records (never localStorage). Guests are invited to create a free account.
export default function ScoreHistory() {
  const { user, openAuth } = useAuth();
  const { openChatbot } = useChatbot();
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!user) {
      setRecords([]);
      return;
    }
    let cancelled = false;
    listRecords("health_score")
      .then((r) => {
        if (!cancelled) setRecords(r);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) {
    return (
      <motion.div
        data-testid="score-history-guest-cta"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-3xl border border-cyan-400/25 bg-gradient-to-br from-[#0B3D91]/20 to-cyan-500/[0.07] p-6 flex flex-col sm:flex-row sm:items-center gap-5"
      >
        <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center shrink-0">
          <Lock className="w-5 h-5 text-cyan-300" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl font-semibold text-white">
            Keep your score history safe
          </h3>
          <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
            Create a free account to save every Health Score privately under
            your account — refreshes and new devices never lose it, and nobody
            else can ever see it.
          </p>
        </div>
        <button
          data-testid="score-history-signin-btn"
          onClick={openAuth}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium shrink-0"
        >
          <Sparkles className="w-4 h-4" />
          Save my results free
        </button>
      </motion.div>
    );
  }

  const data = [...records]
    .reverse()
    .map((r) => ({ date: (r.created_at || "").slice(5, 10), score: r.score }));
  const latestScore = records.length ? records[0].score : null;

  return (
    <motion.div
      data-testid="score-history-panel"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
            <Gauge className="w-3.5 h-3.5" />
            Health Score history
          </div>
          <h3 className="mt-1 font-display text-xl font-semibold text-white">
            Your progress over time
          </h3>
        </div>
        {latestScore != null && (
          <div
            data-testid="score-history-latest"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/30 bg-cyan-500/10"
          >
            <span className="font-display text-xl font-semibold text-cyan-300">{latestScore}</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-400">latest score</span>
          </div>
        )}
      </div>

      {data.length === 0 ? (
        <div className="rounded-2xl border border-white/5 bg-black/30 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <p className="flex-1 text-sm text-zinc-400 leading-relaxed">
            No saved scores yet. Take the free 16-question check-in — every
            result is saved to your private history automatically.
          </p>
          <button
            data-testid="score-history-start-btn"
            onClick={openChatbot}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium shrink-0"
          >
            <Sparkles className="w-4 h-4" />
            Start free check-in
          </button>
        </div>
      ) : (
        <div className="h-52 w-full" data-testid="score-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip
                contentStyle={{
                  background: "#0A0A14",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 12,
                  fontSize: 12,
                  color: "#fff",
                }}
                labelStyle={{ color: "rgba(255,255,255,0.7)" }}
              />
              <Area type="monotone" dataKey="score" stroke="#22d3ee" strokeWidth={2} fill="url(#scoreFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="mt-3 text-[11px] text-zinc-500">
        Stored securely under your account only — never in plain browser
        storage, never visible to anyone else. Manage it from{" "}
        <a href="/history" className="text-cyan-400 hover:underline">
          My Health History
        </a>
        .
      </p>
    </motion.div>
  );
}
